// ─────────────────────────────────────────────
//  native/push.js — FCM push registration (Phase 6, D-06/D-12)
//
//  Native-only: registers the device with FCM via the official
//  @capacitor/push-notifications plugin and files the device token on the
//  signed-in user's doc (users/{uid}.fcmTokens array, merge write — allowed
//  by the self-update rule, which only pins role/isAIC/facilityIds).
//
//  Client-side registration ONLY. Actually SENDING pushes requires a
//  server-side sender (Cloud Functions / Blaze) — that is Phase 5.1 and is
//  documented as a human checkpoint in the Phase 6 SUMMARY.
//
//  Graceful fallback (D-09/D-06): every step is try/caught; on web this is
//  a no-op, on permission-denied we simply stay silent. Push failure must
//  never break login.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import { isNative } from "./platform";
import { isSandboxBoot } from "../sandbox/mode";
import { TASK_CHANNEL_ID } from "../domain/taskPushPayload";

// ⚠️ KEYED ON THE UID, NOT A BARE BOOLEAN. This was `let started = false`, set
// once per app LAUNCH, so the second person to sign in on the same device —
// a shared phone at a nurses' station, a clinician handing the handset over —
// hit the early return and never filed a token. They then received no
// directive, no urgency and no emergency for the rest of the process lifetime,
// with nothing on screen and nothing on the board to say so (the sender's
// ledger just reads "pending" forever). Re-registering for the SAME uid is
// still a no-op, which is all the original guard was for.
let startedFor = null;

// ─────────────────────────────────────────────
//  WHERE A TAPPED NOTIFICATION SHOULD LAND
//
// ⚠️ EVERY PUSH THIS APP SENDS USED TO BE A DEAD END. Only `registration` and
// `registrationError` were ever listened for, so `pushNotificationActionPerformed`
// — the tap — reached nobody. The `taskId` and `alertId` that `taskPushPayload`
// and `pushPayload` are careful to ship were read by NO code at all: a clinician
// tapping "You are covering lunch in OR 5" got the lobby, with no way to find
// what they had just been told. The information was delivered and then thrown
// away on the doorstep.
//
// Alarms need no route: `MobileEmergencyAlarm` / `MobileUrgencyAlert` are
// mounted OUTSIDE the router (MobileShell.jsx:507-511) and surface themselves
// over whatever screen is open. Only the task lane has somewhere to go.
export const PUSH_ROUTE_EVENT = "atc:push-route";
const PENDING_KEY = "atc.pendingPushRoute";

/** Pure: which route a push payload should open. null = stay put. */
export function routeForPush(data) {
  if (!data) return null;
  if (data.taskId) return "/board";
  // An alert's own overlay is global, so the route only has to be somewhere
  // the overlay can sit — never a screen that hides it.
  if (data.alertId) return "/board";
  // ⚠️ A MESSAGE PUSH IS NEW (functions/messagePush.js, 2026-09-11) AND WOULD
  // OTHERWISE BE THE SAME DEAD END this file was written to fix: it rings three
  // times to make somebody look, and a tap landed on the lobby with no way to
  // find what was said. `kind: "message"` is what the payload builder stamps.
  if (data.kind === "message") return "/messages";
  return null;
}

// ⚠️ PARKED, NOT NAVIGATED. A tap on a notification that COLD-STARTS the app
// fires this listener before React Router exists; calling navigate() there
// throws into the plugin's callback and the tap is lost. So the route is parked
// here and in sessionStorage (the listener can also fire before the shell's
// effect runs) and the shell drains it on mount.
let pendingRoute = null;

function parkRoute(route) {
  if (!route) return;
  pendingRoute = route;
  try { sessionStorage.setItem(PENDING_KEY, route); } catch { /* private mode */ }
  try {
    window.dispatchEvent(new CustomEvent(PUSH_ROUTE_EVENT, { detail: { route } }));
  } catch { /* no window */ }
}

/** Take the parked route, if any, and clear it. Safe to call repeatedly. */
export function consumePendingPushRoute() {
  let route = pendingRoute;
  if (!route) {
    try { route = sessionStorage.getItem(PENDING_KEY); } catch { route = null; }
  }
  pendingRoute = null;
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
  return route || null;
}

/**
 * Register this device for push and persist the token for the given uid.
 * Safe to call on every sign-in; no-ops on web and on repeat calls.
 */
// Android 8+ takes sound/vibration/priority from the CHANNEL, not from the
// message: a channelId that was never created silently falls back to the
// default channel, and an emergency then arrives as quietly as a newsletter.
// The Cloud Function sends on these ids (domain/pushPayload buildPushMessage),
// so they must exist before the first push lands. Creating a channel that
// already exists is a no-op, and the user can still override either one in
// Android settings — which is their right, even for an emergency.
export const CHANNELS = [
  {
    id: "atc_emergency",
    name: "Emergencies",
    description: "Room emergencies that need immediate help.",
    importance: 5, // MAX — heads-up, sounds through Do Not Disturb if allowed
    visibility: 1, // public on the lock screen
    sound: "default",
    vibration: true,
    lights: true,
  },
  {
    id: "atc_urgency",
    name: "Urgent requests",
    description: "Your room needs attention.",
    importance: 4, // HIGH — heads-up, no DND override
    visibility: 1,
    sound: "default",
    vibration: true,
  },
  {
    // ⚠️ THE CHANNEL THE DIRECTIVE LANE ALREADY SENDS ON, WHICH DID NOT EXIST.
    // `domain/taskPushPayload.js` has set `channelId: "atc_tasks"` since the
    // lane shipped, and the whole alarm-hygiene argument in that file rests on
    // it — "a channel is what the user silences, and mixing routine board
    // traffic into the emergency channel is how somebody mutes the emergency
    // channel". None of that was true on a device: the id was never registered
    // here, so every directive fell through to FCM's default channel, whose
    // importance is OEM-dependent, and `sound: "default"` in the message was
    // ignored because on API 26+ the CHANNEL owns sound and importance. A
    // clinician could not silence directives separately from emergencies
    // because the channel they would have silenced did not exist.
    //
    // Exactly the failure the comment above this array describes, in the other
    // direction: not an emergency arriving as quietly as a newsletter, but a
    // newsletter arriving whichever way the handset felt like.
    id: TASK_CHANNEL_ID,
    name: "Board directives",
    description: "Instructions from the OR board, and breaks.",
    // ⚠️ 3 = DEFAULT, DELIBERATELY BELOW `atc_urgency`. It posts and sounds but
    // does not push a heads-up banner over what somebody is doing. Routine room
    // churn must never interrupt at the level a room in trouble does; that is
    // the same finite-alarm argument the rest of this lane is built on.
    importance: 3,
    visibility: 1,
    sound: "default",
    vibration: false,
  },
];

async function ensureChannels(PushNotifications) {
  if (typeof PushNotifications.createChannel !== "function") return; // iOS
  for (const channel of CHANNELS) {
    try {
      await PushNotifications.createChannel(channel);
    } catch (err) {
      // Non-fatal: the push still arrives, just on the default channel.
      console.warn("[push] channel setup failed:", channel.id, err?.message);
    }
  }
}

export async function registerPushForUser(uid) {
  // No push in sandbox mode: there is no second user to notify, and asking for
  // a token is an external connection the sandbox promises not to make.
  if (isSandboxBoot()) return;
  if (!isNative() || !uid || startedFor === uid) return;
  startedFor = uid;
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    await ensureChannels(PushNotifications);

    // check-then-request pattern
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") return; // user said no — respect it

    PushNotifications.addListener("registration", async ({ value: token }) => {
      try {
        const [{ db }, { doc, setDoc, arrayUnion, serverTimestamp }] =
          await Promise.all([
            import("../firebase/config"),
            import("firebase/firestore"),
          ]);
        await setDoc(
          doc(db, "users", uid),
          { fcmTokens: arrayUnion(token), fcmUpdatedAt: serverTimestamp() },
          { merge: true }
        );
      } catch (err) {
        // Non-fatal: token just isn't filed this session.
        console.warn("[push] token save failed:", err?.message);
      }
    });

    PushNotifications.addListener("registrationError", (err) => {
      console.warn("[push] registration error:", err?.error);
    });

    // THE TAP. `notification.data` is the FCM data payload built by
    // domain/taskPushPayload + domain/pushPayload.
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      try {
        parkRoute(routeForPush(action?.notification?.data));
      } catch (err) {
        console.warn("[push] tap routing failed:", err?.message);
      }
    });

    // ⚠️ A FOREGROUND PUSH IS SILENT ON ANDROID. The OS hands it straight to
    // this callback and draws nothing, so a directive that arrives while the
    // clinician is looking at the app is invisible unless something in-app
    // says so. Re-broadcast it; surfaces listen and can show their own banner.
    // Deliberately NOT parked as a route: nobody tapped anything, and yanking
    // somebody off the screen they chose is how an app loses trust.
    PushNotifications.addListener("pushNotificationReceived", (notification) => {
      try {
        window.dispatchEvent(
          new CustomEvent("atc:push-foreground", { detail: notification })
        );
      } catch { /* no window */ }
    });

    await PushNotifications.register();
  } catch (err) {
    console.warn("[push] unavailable:", err?.message);
  }
}
